/** RBAC permission definitions */
export const ROLES = ['admin', 'editor', 'viewer', 'agent'] as const
export type Role = (typeof ROLES)[number]

export const PERMISSIONS: Record<Role, string[]> = {
  admin: [
    'tenant:manage',
    'user:manage',
    'doc:*',
    'storage:*',
    'site:*',
    'folder:*',
    'key:*',
    'secret:*',
    'source:*',
    'sync:*',
    'memory:*',
    'audit:read',
  ],
  editor: [
    'doc:create',
    'doc:read',
    'doc:update',
    'doc:delete',
    'doc:search',
    'doc:share',
    'storage:read',
    'storage:upload',
    'storage:delete',
    'site:read',
    'site:upload',
    'site:update',
    'site:delete',
    'site:rollback',
    'site:summary',
    'folder:read',
    'folder:create',
    'folder:update',
    'folder:delete',
    'secret:read',
    'secret:create',
    'secret:update',
    'secret:import',
    'secret:export',
  ],
  viewer: ['doc:read', 'doc:search', 'storage:read', 'site:read', 'folder:read'],
  agent: ['doc:read', 'doc:search'],
}

/** All valid API key scopes */
export const VALID_SCOPES = [
  'doc:read',
  'doc:create',
  'doc:update',
  'doc:delete',
  'doc:search',
  'doc:share',
  'storage:read',
  'storage:upload',
  'storage:delete',
  'site:read',
  'site:upload',
  'site:update',
  'site:delete',
  'site:rollback',
  'site:summary',
  'folder:read',
  'folder:create',
  'folder:update',
  'folder:delete',
  'key:read',
  'key:create',
  'key:update',
  'key:revoke',
  'secret:read',
  'secret:create',
  'secret:update',
  'secret:delete',
  'secret:destroy',
  'secret:reveal',
  'secret:inject',
  'secret:grant',
  'secret:audit',
  'secret:import',
  'secret:export',
  'source:read',
  'source:upsert',
  'sync:read',
  'sync:run',
  'memory:read',
  'memory:write',
  'memory:journal',
] as const

/** Wildcard scopes — grants all actions for a resource */
export const WILDCARD_SCOPES = [
  'doc:*',
  'storage:*',
  'site:*',
  'folder:*',
  'key:*',
  'secret:*',
  'source:*',
  'sync:*',
  'memory:*',
  '*:*',
] as const

/** All accepted scopes (valid + wildcards) */
export const ALL_SCOPES = [...VALID_SCOPES, ...WILDCARD_SCOPES] as const

/** Scope categories for UI grouping */
export const SCOPE_CATEGORIES = {
  doc: {
    label: 'Documents',
    scopes: ['doc:read', 'doc:create', 'doc:update', 'doc:delete', 'doc:search', 'doc:share'],
  },
  storage: { label: 'Storage', scopes: ['storage:read', 'storage:upload', 'storage:delete'] },
  site: {
    label: 'Static Sites',
    scopes: [
      'site:read',
      'site:upload',
      'site:update',
      'site:delete',
      'site:rollback',
      'site:summary',
    ],
  },
  folder: {
    label: 'Folders',
    scopes: ['folder:read', 'folder:create', 'folder:update', 'folder:delete'],
  },
  key: { label: 'API Keys', scopes: ['key:read', 'key:create', 'key:update', 'key:revoke'] },
  secret: {
    label: 'Secrets',
    scopes: [
      'secret:read',
      'secret:create',
      'secret:update',
      'secret:delete',
      'secret:destroy',
      'secret:reveal',
      'secret:inject',
      'secret:grant',
      'secret:audit',
      'secret:import',
      'secret:export',
    ],
  },
  source: { label: 'Source Provenance', scopes: ['source:read', 'source:upsert'] },
  sync: { label: 'Docs Sync', scopes: ['sync:read', 'sync:run'] },
  memory: { label: 'Agent Memory', scopes: ['memory:read', 'memory:write', 'memory:journal'] },
} as const

/** Rate limit configs */
export const RATE_LIMITS = {
  api: { limit: 100, windowSec: 60 },
  search: { limit: 50, windowSec: 60 },
  searchExpand: { limit: 10, windowSec: 60 }, // stricter limit when expand=true (AI cost surface)
  suggest: { limit: 100, windowSec: 60 },
  web: { limit: 1000, windowSec: 60 },
} as const

/** Access levels for documents */
export const ACCESS_LEVELS = ['private', 'specific', 'public'] as const
export type AccessLevel = (typeof ACCESS_LEVELS)[number]

/** Source-aware sync contracts */
export const SOURCE_TYPES = [
  'repo',
  'vault',
  'upload',
  'obsidian',
  'notion',
  'lark',
  'generated',
  'journal',
  'handoff',
] as const
export type SourceType = (typeof SOURCE_TYPES)[number]

export const COMPILE_MODES = ['preserve', 'normalize', 'synthesize'] as const
export type CompileMode = (typeof COMPILE_MODES)[number]

export const SYNC_CONFLICT_POLICIES = ['stop', 'force'] as const
export type SyncConflictPolicy = (typeof SYNC_CONFLICT_POLICIES)[number]

export const SYNC_STATUSES = ['synced', 'conflict'] as const
export type SyncStatus = (typeof SYNC_STATUSES)[number]

export const MEMORY_CLASSES = [
  'project-doc',
  'journal-entry',
  'decision-record',
  'session-summary',
  'agent-observation',
  'plan-report',
  'runbook',
] as const
export type MemoryClass = (typeof MEMORY_CLASSES)[number]

/** Human-governed semantic authority; independent from ownership, access, and sync health. */
export const AUTHORITY_STATES = [
  'active',
  'reference',
  'needs-review',
  'superseded',
  'expired',
] as const
export type AuthorityState = (typeof AUTHORITY_STATES)[number]

/** Latest expiry representable by browser datetime-local controls and ISO dates. */
export const AUTHORITY_EXPIRY_MAX_MS = 253_402_300_799_999

/** Auth token TTLs */
export const TOKEN_TTL = {
  accessToken: 60 * 60 * 1000, // 1 hour
  refreshToken: 30 * 24 * 60 * 60 * 1000, // 30 days
  shareLink: 90 * 24 * 60 * 60 * 1000, // 90 days
} as const

/** API key prefix */
export const API_KEY_PREFIX = 'aw_'

/** Supported AI providers with available models */
export const AI_PROVIDERS = {
  openai: { name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'o3-mini', 'gpt-4-turbo'] },
  anthropic: {
    name: 'Anthropic',
    models: ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-opus-4-6'],
  },
  google: {
    name: 'Google Gemini',
    models: [
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
      'gemini-2.5-pro',
      'gemini-2.0-flash',
      'gemini-3-flash-preview',
      'gemini-3.1-pro-preview',
      'gemini-3.1-flash-lite-preview',
    ],
  },
  openrouter: { name: 'OpenRouter', models: ['auto'] },
  minimax: { name: 'MiniMax', models: ['MiniMax-M1', 'MiniMax-T1'] },
  alibaba: { name: 'Alibaba', models: ['qwen-turbo', 'qwen-plus', 'qwen-max'] },
} as const

/** AI rate limit: requests per user per minute */
export const AI_RATE_LIMIT = { maxRequests: 15, intervalMs: 60_000 } as const
